<template>
    <div
        class="flex flex-col w-full gap-4 bg-white shadow-md sm:rounded-xl"
        :class="status === 'review' ? 'py-4' : 'p-4'"
    >
        <slot v-if="custom" />

        <!-- Quiz Section -->
        <template v-if="status === 'quiz'">
            <div v-if="quiz" class="flex flex-col gap-4" :key="quizStore.step">
                <h1 class="m-0 text-black">
                    Question n° {{ quiz.questions[quizStore.step].id }}
                </h1>
                <p class="text-xl">
                    {{ quiz.questions[quizStore.step].description[locale] }}
                </p>

                <ul class="space-y-2">
                    <li
                        v-for="(answer, index) in quiz.questions[quizStore.step]
                            .answers"
                        :key="index"
                        class="flex items-center gap-2 w-full"
                    >
                        <input
                            :type="isSingleCorrect ? 'radio' : 'checkbox'"
                            :id="`answer-${index}`"
                            :name="`answer-${quizStore.step}`"
                            class="m-0 w-5 min-w-5 accent-green"
                            @change="
                                () =>
                                    updateSelectedAnswer(
                                        isSingleCorrect,
                                        selectedAnswer,
                                        quizStore.step,
                                        index,
                                    )
                            "
                            :value="index"
                        />
                        <label
                            :for="`answer-${index}`"
                            class="m-0 text-gray-700 break-words w-full"
                        >
                            {{ translatedText(answer.description) }}
                        </label>
                    </li>
                </ul>

                <primary-button color="green" class="w-fit" @click="nextStep">
                    {{ $translate('public.quiz.buttons.next') }}
                </primary-button>
            </div>
        </template>

        <!-- Review Section -->
        <template v-else-if="status === 'review'">
            <div class="px-4 mt-4 space-y-8 font-bold">
                <h1 class="m-0 text-3xl text-black">Results</h1>
                <h2 class="text-lg">Question n° {{ quizStore.step + 1 }}</h2>
                <h3 class="text-lg">
                    {{
                        translatedText(
                            quiz.questions[quizStore.step].description,
                        )
                    }}
                </h3>
            </div>

            <div>
                <ul class="flex flex-col md:w-1/2">
                    <li
                        v-for="(answer, index) in quiz.questions[quizStore.step]
                            .answers"
                        :key="index"
                        :class="[
                            'p-4 flex justify-between items-center',
                            {
                                'bg-green-transparent':
                                    (savedAnswers[quizStore.step].includes(
                                        index,
                                    ) &&
                                        answer.is_correct) ||
                                    answer.is_correct,
                                'bg-red-transparent':
                                    savedAnswers[quizStore.step].includes(
                                        index,
                                    ) && !answer.is_correct,
                            },
                        ]"
                    >
                        <div class="flex items-center gap-2">
                            <input
                                :type="isSingleCorrect ? 'radio' : 'checkbox'"
                                :id="`answer-${index}`"
                                :name="`answer-${quizStore.step}`"
                                class="w-5 m-0"
                                :value="index"
                                :checked="
                                    savedAnswers[quizStore.step].includes(index)
                                "
                                disabled
                            />
                            <p>{{ translatedText(answer.description) }}</p>
                        </div>
                        <span>
                            <svg
                                v-if="
                                    answer.is_correct &&
                                    savedAnswers[quizStore.step].includes(index)
                                "
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="text-green"
                            >
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            <svg
                                v-if="
                                    answer.is_correct &&
                                    !savedAnswers[quizStore.step].includes(
                                        index,
                                    )
                                "
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="text-green"
                            >
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            <svg
                                v-else-if="
                                    !answer.is_correct &&
                                    savedAnswers[quizStore.step].includes(index)
                                "
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#d60b52"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                            </svg>
                        </span>
                    </li>
                </ul>

                <p class="flex flex-col gap-2 p-8">
                    <span class="text-lg font-bold underline text-green">{{
                        $translate('public.quiz.explanation')
                    }}</span>
                    <span>{{
                        quiz.questions[quizStore.step].explanation[locale]
                    }}</span>
                </p>

                <div class="flex items-center gap-2 p-4">
                    <primary-button
                        class="w-fit"
                        @click="() => emit('update:status', 'quiz')"
                    >
                        {{ $translate('public.quiz.buttons.back') }}
                    </primary-button>
                    <primary-button color="green" @click="nextStep()">
                        {{ $translate('public.quiz.buttons.next') }}
                    </primary-button>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { useApiStore } from '@/js/stores/apiStore';
import { usePopupStore } from '@/js/stores/popupStore';
import { useQuizStore } from '@/js/stores/quizStore';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const apiStore = useApiStore();
const popupStore = usePopupStore();
const quizStore = useQuizStore();

const { locale, t } = useI18n();

const selectedAnswer = ref({});
const score = ref(0);
const savedAnswers = quizStore.selectedAnswer;

const emit = defineEmits(['update:status', 'update:score']);
const props = defineProps({
    custom: { type: Boolean, required: false },
    quiz: { type: Object, required: false },
    status: { type: String, required: false },
    email: { type: String, required: false },
});

const translatedText = textObject =>
    textObject?.[locale.value] || 'No translation available';

const nextStep = async () => {
    if (props.status === 'quiz') {
        if (selectedAnswer.value[quizStore.step]?.length > 0) {
            quizStore.nextStep(props.quiz.questions.length);
            if (quizStore.step === 0) {
                quizStore.setSelectedAnswer(selectedAnswer.value);
                await attempt();
                emit('update:status', 'result');
                emit('update:score', score.value);
            }
        } else {
            popupStore.show('warning', t('public.quiz.select'));
        }
    } else {
        quizStore.nextStep(props.quiz.questions.length);
        if (quizStore.step === 0) {
            emit('update:status', 'quiz');
        }
    }
};

const isSingleCorrect = computed(() => {
    const question = props.quiz?.questions[quizStore.step];
    return question?.answers.filter(answer => answer.is_correct).length === 1;
});

const updateSelectedAnswer = (isSingleCorrect, selectedAnswer, step, index) => {
    // Ensure the step key is initialized as an empty array
    if (!selectedAnswer[step]) {
        selectedAnswer[step] = [];
    }

    if (isSingleCorrect) {
        // Replace the current selection with the new one
        selectedAnswer[step] = [index];
    } else {
        // Add or remove the answer based on its presence
        if (selectedAnswer[step].includes(index)) {
            selectedAnswer[step] = selectedAnswer[step].filter(
                answer => answer !== index,
            );
        } else {
            selectedAnswer[step].push(index);
        }
    }
};

const attempt = async () => {
    try {
        const response = await apiStore.apiPost('/api/quiz/attempt', {
            email: props.email || quizStore.getUserEmail(),
            answers: selectedAnswer.value,
        });
        if (response.success) {
            popupStore.show('success', t('public.quiz.success'));
            score.value = response.result.score;
        } else {
            popupStore.show('warning', t('public.quiz.errors.quiz_fail'));
        }
    } catch {
        popupStore.show('warning', t('quiz.errors.general'));
    }
};
</script>
